import useCurrentPlan from '@//hooks/useCurrentPlan/useCurrentPlan';
import { useApolloMutation, useApolloQuery } from '@/gql/apolloWrapper';
import {
  AvailableProduct,
  BillingPeriod,
  ProductKey,
  useGetAvailableProductsQuery,
  usePurchaseCreditsMutation,
  usePurchaseSubscriptionMutation,
} from '@/gql/generated';
import useCurrentCompany from '@/hooks/useCurrentCompany';
import usePaymentMethods from '@/modules/Settings/hooks/usePaymentMethods';
import { formatCurrency } from '@/utils/currency';
import isAdmin from '@/utils/isAdmin';
import { TypeDisplay } from '@bambeehr/pollen';
import useNotifications from '@bambeehr/use-notifications';
import { computed, ComputedRef, ref } from '@nuxtjs/composition-api';
import useProductCopy from './const/productCopy';

export enum PriceTerms {
  MONTHLY = 'month',
  YEARLY = 'year',
}

export enum WebinerEvents {
  WEBINAR_PAGE_VIEW = 'WEBINAR_PAGE_VIEW',
  WEBINAR_PURCHASE_MODAL_OPENED = 'WEBINAR_PURCHASE_MODAL_OPENED',
  WEBINAR_PURCHASED = 'WEBINAR_PURCHASED',
  WEBINAR_SCHEDULED = 'WEBINAR_SCHEDULED',
  WEBINAR_ACH_ADDED = 'WEBINAR_ACH_ADDED',
  WEBINAR_VIEWED_ACH_FORM = 'WEBINAR_VIEWED_ACH_FORM',
}

let track;

const isLoading = ref(false);
const isSubscription = ref(true);
const displayProduct = ref();
const allProducts = ref<AvailableProduct[]>([]);
const shouldShowPaymentMethodModal = ref(false);
const showPurchaseModal = ref(false);
const employeeSizeRange = ref();
const currentTier = ref<number>();
const showAddOns = ref(false);

const { copy, hasACH, isAboveTierThreshold } = useProductCopy();
const setupHook = (force = false) => {
  const { companyId } = useCurrentCompany();
  const { currentPlan } = useCurrentPlan();
  track = (event, data) => {}; // Tracking stub
  employeeSizeRange.value = currentPlan.value?.employeeSizeMapping;
  currentTier.value = currentPlan.value?.tierNumber;

  if (isAdmin.value) {
    const { onResult } = useApolloQuery(
      useGetAvailableProductsQuery,
      { data: { companyId: companyId.value } },
      undefined,
      { force }
    );

    onResult((result: any) => {
      const res = result?.getAvailableProducts;
      allProducts.value = res || [];
      isLoading.value = false;
    });
  }
};

const refresh = () => {
  setupHook(true);
};

const productAllowance = computed(
  () =>
    displayProduct.value !== ProductKey.WebinarWorkplaceViolence &&
    allProducts.value.find((p) => p.key === displayProduct.value)
      ?.monthlyAllowance
);

const currentCopy = computed(() =>
  displayProduct.value ? copy.value[displayProduct.value] : null
);

const currentTermModel = ref(BillingPeriod.Month);
export enum PurchaseOptions {
  ACH = 'ach',
  ONE_TIME = 'one-time',
  WEBINAR_ONE_TIME = 'webinar-one-time',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  WEBINAR_YEARLY = 'webinar-yearly',
  BASIC = 'basic',
  PREMIUM = 'premium',
}
export interface ProductOption {
  id: PurchaseOptions;
  key?: ProductKey;
  label: string;
  labelComponent?: any;
  monthlyPrice: number | null;
  formattedMonthlyPrice?: string | null;
  formattedAnnualPrice?: string | null;
  price: number;
  formattedPrice?: string;
  value: BillingPeriod;
  savingPercent: number;
  description?: string;
  discountPrice?: string;
  hasDiscount?: boolean;
  disabled?: boolean;
  itemValue?: string;
  secondaryDescription?: string;
}

export const formatPrice = (price) => formatCurrency(price / 100);

const currentProductOptions = computed<ProductOption[]>(() => {
  const filtered = allProducts.value.filter(
    (p) => p.key === displayProduct.value
  );

  if (filtered.length === 0) {
    return [];
  }

  if (displayProduct.value === ProductKey.WebinarWorkplaceViolence) {
    const item = filtered.find(
      (p) =>
        p.billingPeriod === PriceTerms.YEARLY &&
        p.tier === (currentTier.value ?? 1)
    );
    if (!item) {
      return [];
    }

    currentTermModel.value = BillingPeriod.Year;
    const webinarLabel = employeeSizeRange.value
      ? `Price for ${employeeSizeRange.value} employees`
      : 'Price for one year';
    const webinarPurchase = {
      id: PurchaseOptions.WEBINAR_YEARLY,
      label: webinarLabel,
      labelComponent: TypeDisplay,
      monthlyPrice: null,
      price: item.subscriptionPrice ?? 0,
      formattedMonthlyPrice: formatPrice(item.subscriptionPrice ?? 0), // not actually the monthly price, but this value is used to display the price
      value: BillingPeriod.Year,
      savingPercent: 0,
      description: '',
      chargeDescription: `You will be charged ${formatPrice(
        item.subscriptionPrice ?? 0
      )} + tax on an annual recurring basis.`,
    };

    const priceOptions: ProductOption[] = [webinarPurchase];

    return priceOptions;
  }

  if (isSubscription.value) {
    const monthly = filtered.find(
      (p) => p.billingPeriod === PriceTerms.MONTHLY
    );
    const yearly = filtered.find((p) => p.billingPeriod === PriceTerms.YEARLY);

    const formatPrice = (price: number, divisor = 100) =>
      formatCurrency(price / divisor, 2);

    return [
      {
        id: PurchaseOptions.MONTHLY,
        label: 'Monthly',
        labelComponent: TypeDisplay,
        monthlyPrice: monthly?.subscriptionPrice ?? 0,
        formattedMonthlyPrice: formatPrice(monthly?.subscriptionPrice ?? 0),
        price: monthly?.subscriptionPrice ?? 0,
        formattedPrice: formatPrice(monthly?.subscriptionPrice ?? 0),
        value: BillingPeriod.Month,
        savingPercent: 0,
        description: 'per month',
      },
      {
        id: PurchaseOptions.YEARLY,
        label: 'Annual',
        labelComponent: TypeDisplay,
        monthlyPrice: (yearly?.subscriptionPrice ?? 0) / 12,
        formattedMonthlyPrice: formatPrice(
          (yearly?.subscriptionPrice ?? 0) / 12
        ),
        formattedAnnualPrice: formatPrice(yearly?.subscriptionPrice ?? 0),
        price: yearly?.subscriptionPrice ?? 0,
        formattedPrice: formatPrice((yearly?.subscriptionPrice ?? 0) / 12),
        value: BillingPeriod.Year,
        savingPercent: 20,
        description: 'per month billed annually',
      },
    ];
  }

  return [];
});

const currentProductTermInfo = computed(() => {
  if (!currentProductOptions.value) {
    return null;
  }

  return currentProductOptions.value.find(
    (p) => p.value === currentTermModel.value
  );
});

const isSubscribing = ref(false);
const onSubscribed = ref();

const handlePurchase = async (productOption) => {
  const { addSuccess } = useNotifications();
  track('purchase-started', productOption);
  if (isSubscription.value) {
    const { onDone, mutate } = useApolloMutation(
      usePurchaseSubscriptionMutation,
      {
        pending: isSubscribing,
      }
    );
    onDone((result: any) => {
      const res = result?.purchaseSubscription;
      addSuccess(`${currentCopy.value.title} subscription added!`);
      onSubscribed?.value?.(res);
    });
    mutate({
      data: {
        productKey: displayProduct.value,
        billingPeriod: currentTermModel.value,
      },
    });

    return;
  }

  const { onDone, mutate } = useApolloMutation(usePurchaseCreditsMutation, {
    pending: isSubscribing,
  });

  onDone((result: any) => {
    const res = result?.purchaseCredits;
    addSuccess(`${currentCopy.value.title} subscription added!`);
    onSubscribed?.value?.(res);
  });

  mutate({
    data: {
      productKey: displayProduct.value,
      quantity: 1,
    },
  });
};

const handleCTAClicked = async (productOption) => {
  if (productOption.id === PurchaseOptions.ACH && !hasACH.value) {
    showPurchaseModal.value = false;
    shouldShowPaymentMethodModal.value = true;

    track(WebinerEvents.WEBINAR_VIEWED_ACH_FORM);

    return;
  }
  await handlePurchase(productOption);
};

let init;

const showPaymentMethodModal = () => {
  shouldShowPaymentMethodModal.value = true;
  track(WebinerEvents.WEBINAR_VIEWED_ACH_FORM);
};

const handleAdd = () => {
  const { refetch: getBillingInfoRefetch } = usePaymentMethods();

  getBillingInfoRefetch();
  refresh();
  shouldShowPaymentMethodModal.value = false;
  showPurchaseModal.value = true;
  track(WebinerEvents.WEBINAR_ACH_ADDED);
};

const useProductSelector = (product?: ProductKey, isSub?: boolean) => {
  if (product) {
    displayProduct.value = product;
    isSubscription.value = isSub ?? true;
  }
  if (!init) {
    setupHook();
    init = true;
  }

  return {
    isLoading,
    currentCopy,
    currentProductOptions,
    currentTermModel,
    productAllowance,
    currentProductTermInfo,
    onSubscribed,
    handlePurchase,
    isSubscribing,
    isSubscription,
    refresh,
    shouldShowPaymentMethodModal,
    showPaymentMethodModal,
    handleAdd,
    showPurchaseModal,
    hasACH,
    handleCTAClicked,
    employeeSizeRange,
    displayProduct,
    showAddOns,
  };
};

export default useProductSelector;
